import React, { useEffect, useState, useRef } from 'react';
import { BASE_URL } from '../config';
import { showError, showSuccess } from '../utils/dialog';

function UnsavedChangesDialog({ isOpen, onSaveAndContinue, onDiscardAndContinue, onReview }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900">You Have Unsaved Changes</h4>
        <p className="mt-2 text-sm text-gray-600">What would you like to do with your changes?</p>
        <div className="mt-6 flex items-center gap-3">
          <button type="button" onClick={onSaveAndContinue} className="px-4 py-2 rounded-lg bg-sidebar-active text-white text-sm font-semibold hover:bg-red-600">
            SAVE & CONTINUE
          </button>
          <button type="button" onClick={onDiscardAndContinue} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            DISCARD CHANGES & CONTINUE
          </button>
          <button type="button" onClick={onReview} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50">
            REVIEW CHANGES
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PositionDetailModal({ position, isOpen, onClose, onSaved, readOnly = false }) {
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);

  // Dropdown data for Skills / Certifications (loaded from backend)
  const [skillOptions, setSkillOptions] = useState([]);
  const [certOptions, setCertOptions] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [certsLoading, setCertsLoading] = useState(false);

  // Track selected IDs for multi-select dropdowns (used to derive names)
  const [selectedSkillIds, setSelectedSkillIds] = useState([]);
  const [selectedCertIds, setSelectedCertIds] = useState([]);

  // UI state for typeahead-style dropdowns
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [certsOpen, setCertsOpen] = useState(false);
  const [skillsSearch, setSkillsSearch] = useState('');
  const [certsSearch, setCertsSearch] = useState('');

  const skillsRef = useRef(null);
  const certsRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setForm(null);
      setOriginal(null);
      setSelectedSkillIds([]);
      setSelectedCertIds([]);
      return;
    }

    // initialize from provided position object
    const p = position || {};
    console.log('📋 [POSITION MODAL] Initializing with position:', p);
    console.log('📋 [POSITION MODAL] divisionName:', p.divisionName);
    console.log('📋 [POSITION MODAL] division:', p.division);
    console.log('📋 [POSITION MODAL] divisions:', p.divisions);
    
    const skills = p.skills || '';
    const certs = p.certifications || '';
    const requirements =
      p.requirements ||
      ((skills || '') + (skills && certs ? ', ' : '') + (certs || ''));
    const init = {
      id: p.id || p.position_id || null,
      name: p.name || '', // non-editable
      state: p.state || '',
      division: p.divisionName || p.division || p.divisions || '', // non-editable - check all possible field names
      clientName: p.clientName || p.client_name || '',
      bill_rate: p.bill_rate == null ? '' : String(p.bill_rate),
      pay_rate: p.pay_rate == null ? '' : String(p.pay_rate),
      unpaid_break: !!p.unpaid_break,
      uniform: p.uniform || '',
      skills: skills || '',
      certifications: certs || '',
      requirements: requirements || '',
    };
    console.log('📋 [POSITION MODAL] Form initialized with division:', init.division);
    setForm(init);
    setOriginal(JSON.parse(JSON.stringify(init)));
  }, [isOpen, position]);

  // Load skills and certifications from backend when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchSkills = async () => {
      try {
        setSkillsLoading(true);
        const res = await fetch(`${BASE_URL}/api/personnel/skills`);
        const data = await res.json();
        setSkillOptions(data?.data || []);

        // Try to pre-select based on existing skills string (comma-separated names)
        if (form && form.skills) {
          const names = String(form.skills)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          const ids = (data?.data || [])
            .filter((opt) => names.includes(opt.name))
            .map((opt) => String(opt.id));
          setSelectedSkillIds(ids);
        }
      } catch (err) {
        console.error('Failed to load skills list', err);
      } finally {
        setSkillsLoading(false);
      }
    };

    const fetchCerts = async () => {
      try {
        setCertsLoading(true);
        const res = await fetch(`${BASE_URL}/api/personnel/certifications`);
        const data = await res.json();
        setCertOptions(data?.data || []);

        if (form && form.certifications) {
          const names = String(form.certifications)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          const ids = (data?.data || [])
            .filter((opt) => names.includes(opt.name))
            .map((opt) => String(opt.certification_id || opt.id));
          setSelectedCertIds(ids);
        }
      } catch (err) {
        console.error('Failed to load certifications list', err);
      } finally {
        setCertsLoading(false);
      }
    };

    fetchSkills();
    fetchCerts();
  }, [isOpen, form]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (skillsRef.current && !skillsRef.current.contains(e.target)) {
        setSkillsOpen(false);
      }
      if (certsRef.current && !certsRef.current.contains(e.target)) {
        setCertsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen || !form) return null;

  const handleChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const applySkillIds = (ids) => {
    setSelectedSkillIds(ids);
    const names = skillOptions
      .filter((opt) => ids.includes(String(opt.id)))
      .map((opt) => opt.name);
    handleChange('skills', names.join(', '));
  };

  const applyCertIds = (ids) => {
    setSelectedCertIds(ids);
    const names = certOptions
      .filter((opt) => ids.includes(String(opt.certification_id || opt.id)))
      .map((opt) => opt.name);
    handleChange('certifications', names.join(', '));
  };

  const toggleSkill = (id) => {
    setSelectedSkillIds((prev) => {
      const idStr = String(id);
      const exists = prev.includes(idStr);
      const next = exists ? prev.filter((v) => v !== idStr) : [...prev, idStr];
      // also update form.skills
      const names = skillOptions
        .filter((opt) => next.includes(String(opt.id)))
        .map((opt) => opt.name);
      handleChange('skills', names.join(', '));
      return next;
    });
  };

  const toggleCert = (id) => {
    setSelectedCertIds((prev) => {
      const idStr = String(id);
      const exists = prev.includes(idStr);
      const next = exists ? prev.filter((v) => v !== idStr) : [...prev, idStr];
      const names = certOptions
        .filter((opt) => next.includes(String(opt.certification_id || opt.id)))
        .map((opt) => opt.name);
      handleChange('certifications', names.join(', '));
      return next;
    });
  };

  const hasUnsavedChanges = () => {
    if (!original || !form) return false;
    try {
      return JSON.stringify(form) !== JSON.stringify(original);
    } catch (_) {
      return false;
    }
  };

  const requestClose = () => {
    if (hasUnsavedChanges()) {
      setUnsavedPromptOpen(true);
      return;
    }
    onClose?.();
  };

  const handleSave = async () => {
    if (!form || !form.id) return showError('No position selected');
    setSaving(true);
    try {
      // compose requirements from skills / certifications if present
      const parts = [];
      if (form.skills && String(form.skills).trim()) parts.push(String(form.skills).trim());
      if (form.certifications && String(form.certifications).trim()) parts.push(String(form.certifications).trim());
      const requirements = parts.join(', ');

      // Prepare skill / certification ids for backend (for proper Requirements)
      const skill_ids = selectedSkillIds
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n));
      const certification_ids = selectedCertIds
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n));

      const payload = {
        name: form.name, // send existing name back
        bill_rate: form.bill_rate === '' ? null : Number(form.bill_rate),
        pay_rate: form.pay_rate === '' ? null : Number(form.pay_rate),
        // backend expects a numeric flag; use 1/0 like other flows
        unpaid_break: form.unpaid_break ? 1 : 0,
        uniform: form.uniform || null,
        requirements: requirements || form.requirements || null,
        skill_ids,
        certification_ids,
      };

      // backend expects client id in the path: /api/clients/:clientId/positions/:positionId
      const clientId = position?.clientId || position?.client_id || position?.clientId || position?.client || position?.clientId;
      const url = clientId ? `${BASE_URL}/api/clients/${clientId}/positions/${form.id}` : `${BASE_URL}/api/clients/positions/${form.id}`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || 'Save failed');
      showSuccess('Position saved');
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Position save failed', err);
      showError(err.message || 'Server error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white w-full max-w-sm md:max-w-xl rounded-lg shadow-lg overflow-hidden max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <div className="text-sm font-semibold">Edit Position</div>
            <div className="text-lg font-medium flex items-center gap-2">
              <span>{form.name || 'Position'}</span>
              {String(form.state || '').toLowerCase() === 'archived' && (
                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-semibold text-yellow-800">
                  Archived
                </span>
              )}
            </div>
          </div>
          <button onClick={requestClose} className="text-gray-500 hover:text-gray-700">✖</button>
        </div>

        <div className="p-3 md:p-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 56px)' }}>
          <fieldset disabled={readOnly} className="border-0 m-0 p-0 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium">Position</label>
            <input type="text" value={form.name} readOnly className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100" />
          </div>

          <div>
            <label className="block text-sm font-medium">Division</label>
            <input type="text" value={form.division || ''} readOnly className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100" />
          </div>

          <div>
            <label className="block text-sm font-medium">Client</label>
            <input type="text" value={form.clientName || ''} readOnly className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100" />
          </div>

          <div>
            <label className="block text-sm font-medium">Bill Rate</label>
            <div className="flex items-center gap-2">
              <span className="px-3 py-2 bg-gray-100 border border-gray-300 rounded">$</span>
              <input type="text" value={form.bill_rate || ''} onChange={(e) => handleChange('bill_rate', e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium">Pay Rate</label>
            <div className="flex items-center gap-2">
              <span className="px-3 py-2 bg-gray-100 border border-gray-300 rounded">$</span>
              <input type="text" value={form.pay_rate || ''} onChange={(e) => handleChange('pay_rate', e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2" />
            </div>
          </div>

            <div className="col-span-1 sm:col-span-2 flex items-center gap-2">
            <input id="unpaid_break" type="checkbox" checked={!!form.unpaid_break} onChange={(e) => handleChange('unpaid_break', e.target.checked)} />
            <label htmlFor="unpaid_break" className="text-sm">Unpaid 30-minute break</label>
          </div>

          <div className="col-span-1 sm:col-span-2">
            <label className="block text-sm font-medium">Uniform</label>
            <input type="text" value={form.uniform || ''} onChange={(e) => handleChange('uniform', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5" />
          </div>

          <div className="col-span-1 sm:col-span-2" ref={skillsRef}>
            <label className="block text-sm font-medium">Skills</label>
            <div className="relative">
              <input
                type="text"
                value={skillsSearch || form.skills || ''}
                onChange={(e) => {
                  setSkillsSearch(e.target.value);
                  setSkillsOpen(true);
                }}
                onFocus={() => setSkillsOpen(true)}
                className="w-full border border-gray-300 rounded px-2 py-1.5"
                placeholder="Start typing to search skills..."
              />
              {skillsOpen && (
                <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded shadow-sm">
                  {skillsLoading ? (
                    <div className="px-3 py-2 text-xs text-gray-500">
                      Loading skills...
                    </div>
                  ) : (
                    skillOptions
                      .filter((s) => {
                        const name = typeof s.name === 'string' ? s.name : String(s.name || '');
                        return (skillsSearch || '').trim()
                          ? name.toLowerCase().includes(skillsSearch.toLowerCase())
                          : true;
                      })
                      .map((s) => {
                        const idStr = String(s.id);
                        const active = selectedSkillIds.includes(idStr);
                        const skillName = typeof s.name === 'string' ? s.name : String(s.name || '');
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => toggleSkill(idStr)}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                              active ? 'bg-red-50 text-red-600' : ''
                            }`}
                          >
                            {skillName}
                            {active && (
                              <span className="ml-1 text-[10px] uppercase tracking-wide">
                                (selected)
                              </span>
                            )}
                          </button>
                        );
                      })
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="col-span-1 sm:col-span-2" ref={certsRef}>
            <label className="block text-sm font-medium">Certifications</label>
            <div className="relative">
              <input
                type="text"
                value={certsSearch || form.certifications || ''}
                onChange={(e) => {
                  setCertsSearch(e.target.value);
                  setCertsOpen(true);
                }}
                onFocus={() => setCertsOpen(true)}
                className="w-full border border-gray-300 rounded px-2 py-1.5"
                placeholder="Start typing to search certifications..."
              />
              {certsOpen && (
                <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded shadow-sm">
                  {certsLoading ? (
                    <div className="px-3 py-2 text-xs text-gray-500">
                      Loading certifications...
                    </div>
                  ) : (
                    certOptions
                      .filter((c) => {
                        const name = typeof c.name === 'string' ? c.name : String(c.name || '');
                        return (certsSearch || '').trim()
                          ? name.toLowerCase().includes(certsSearch.toLowerCase())
                          : true;
                      })
                      .map((c) => {
                        const idStr = String(c.certification_id || c.id);
                        const active = selectedCertIds.includes(idStr);
                        const certName = typeof c.name === 'string' ? c.name : String(c.name || '');
                        return (
                          <button
                            key={idStr}
                            type="button"
                            onClick={() => toggleCert(idStr)}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                              active ? 'bg-red-50 text-red-600' : ''
                            }`}
                          >
                            {certName}
                            {active && (
                              <span className="ml-1 text-[10px] uppercase tracking-wide">
                                (selected)
                              </span>
                            )}
                          </button>
                        );
                      })
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="col-span-1 sm:col-span-2 flex justify-end gap-2 pt-2">
            <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 bg-sidebar-active text-white rounded text-sm">Save</button>
            <button onClick={requestClose} type="button" className="px-3 py-1.5 border rounded text-sm">Cancel</button>
            <button onClick={requestClose} type="button" className="px-3 py-1.5 text-sm">Close</button>
          </div>
        </div>
        </fieldset>
        </div>
      </div>
      <UnsavedChangesDialog
        isOpen={unsavedPromptOpen}
        onSaveAndContinue={async () => {
          setUnsavedPromptOpen(false);
          await handleSave();
        }}
        onDiscardAndContinue={() => {
          setUnsavedPromptOpen(false);
          setForm(JSON.parse(JSON.stringify(original || {})));
          onClose?.();
        }}
        onReview={() => setUnsavedPromptOpen(false)}
      />
    </div>
  );
}
